import { defineStore } from 'pinia'
import { api } from '@/plugins/axios'
import event from '@/plugins/events'
const storage = window.localStorage

export const useUserStore = defineStore('user', {
  state: () => ({
    user: JSON.parse(storage.getItem('AgentUser') || '{}'),
    token: storage.getItem('AgentToken'),
    pinNav: storage.getItem('PinNav') == 'true',
    teams: JSON.parse(storage.getItem('userTeams') || '[]'),
    roles: JSON.parse(storage.getItem('userRoles') || '[]')
  }),
  actions: {
    setToken(token) {
      this.token = token
      storage.setItem('AgentToken', this.token)
    },
    login(user, token) {
      this.user = Object.assign({}, user)
      storage.setItem('AgentUser', JSON.stringify(this.user))
      this.setToken(token)
    },
    async logout() {
      this.user = Object.assign({}, {})
      this.token = null
      storage.removeItem('AgentUser')
      storage.removeItem('AgentToken')
      storage.removeItem('PinNav')
      this.setTeams()
      this.setUserRoles()
      try {
        await api.delete('/login')
      } catch (e) {
        event.error(e)
      }
    },
    setUserId(user_id) {
      this.user = Object.assign(this.user, {user_id})
      storage.setItem('AgentUser', JSON.stringify(this.user))
    },
    setEmail(email) {
      this.user = Object.assign(this.user, {email})
      storage.setItem('AgentUser', JSON.stringify(this.user))
    },
    setWebexLinked(linked) {
      this.user = { ...this.user, webex_linked: !!linked }
      storage.setItem('AgentUser', JSON.stringify(this.user))
    },
    setPin(val) {
      this.pinNav = val
      storage.setItem('PinNav', this.pinNav)
    },
    setTeams(teams) {
      this.teams = teams
      teams ? storage.setItem('userTeams', JSON.stringify(this.teams)) : storage.removeItem('userTeams')
    },
    setUserRoles(roles) {
      this.roles = roles
      roles ? storage.setItem('userRoles', JSON.stringify(this.roles)) : storage.removeItem('userRoles')
    }
  },
  getters: {
    getUser: ({user}) => user,
    getUserId: ({user}) => user?.user_id,
    getUserName: ({user}) => user?.first_name ? `${user.first_name} ${user.last_name}` : null,
    getFirstName: ({user}) => user?.first_name,
    getLastName: ({user}) => user?.last_name,
    getToken: ({token}) => token,
    getRole: ({user}) => user?.role_name,
    getRoleId: ({user}) => user?.role_id,
    getPermissions: ({ user }) => user.permissions || {},
    isAdmin: ({user}) => user?.role_id == 1,
    getWebexEnabled: ({ user }) => !!user?.webex_enabled,
    getWebexLinked: ({ user }) => !!user?.webex_linked,
    canAccessSearch: ({ user }) => (user.permissions || {}).customer_support !== false,
    // Gates the whole "Product" admin group (TFNs/Callouts/Offers), not just TFNs --
    // role-based/team-name fallbacks are deliberately not used here; Admin or the
    // explicit `product_admin` permission (which can live on a role, team, or user
    // row, per getUser()'s merge) are the only ways in.
    canAccessProductAdmin: ({ user }) => user?.role_id == 1 || !!(user.permissions || {}).product_admin,
    // Admin or an explicit `qa_tools` permission (which can live on a team, via the
    // JSONB permissions column on user_teams) are the only ways in.
    canAccessQaTools: ({ user }) => user?.role_id == 1 || !!(user.permissions || {}).qa_tools,
    // Read tier for the Products admin page (category settings + SKU overrides) only --
    // Admin, product_admin (full CRUD), or qa_tools (view-only, see canManageProductAdmin
    // below) -- matching cash-server's admin/products route group. Callouts/TFNs/Offers
    // have no qa_tools tier at all, so they stay on canAccessProductAdmin.
    canViewProductAdmin: ({ user }) =>
      user?.role_id == 1 || !!(user.permissions || {}).product_admin || !!(user.permissions || {}).qa_tools,
    // Gates the "Marketing" admin group (Source Codes) -- Admin or the explicit
    // `marketing_admin` permission (role/team/user JSONB, per getUser()'s merge).
    canAccessMarketingAdmin: ({ user }) => user?.role_id == 1 || !!(user.permissions || {}).marketing_admin,
    // Admin nav group visibility is derived from its own sub-options' gates
    // (Users/Teams/Roles need isAdmin, Product needs canViewProductAdmin, QA
    // Tools needs canAccessQaTools, Marketing needs canAccessMarketingAdmin) --
    // not a separate flag, so a permission-only (non-admin) user who can reach
    // any nested item still sees the group.
    canAccessAdminGroup() {
      return this.isAdmin || this.canViewProductAdmin || this.canAccessQaTools || this.canAccessMarketingAdmin
    },
    // Write-action gating for the Products admin page -- Admin or a delegated
    // product_admin permission holder may mutate; QA reaches the page (see router,
    // via canViewProductAdmin) but stays view-only, matching the cash-server route's
    // requireProductAdmin guard.
    canManageProductAdmin: ({ user }) => user?.role_id == 1 || !!(user.permissions || {}).product_admin,
    // Generic delegated-permission check (e.g. 'product_admin') -- for write-action gating
    // on pages where Admin and QA both reach the page but only Admin/permission-holders
    // may mutate, mirroring the matching three-tier check on the cash-server route.
    hasPermission: ({ user }) => (flag) => !!(user.permissions || {})[flag],
    // Single source of truth for "where does this user go by default". A role without
    // Search access can declare its own `landing_page` permission naming the route to
    // land on instead; absent that, they fall back to the first admin-group page their
    // permissions actually reach (mirrors canAccessRoute's per-route gates below, so
    // this never points at a route the guard will immediately bounce them out of);
    // with none of the above, there's nowhere for them to go (null -> noAccess).
    landingRoute() {
      if (this.canAccessSearch) return 'search'
      if (this.getPermissions.landing_page) return this.getPermissions.landing_page
      if (this.isAdmin) return 'admin-users'
      if (this.canAccessProductAdmin) return 'admin-tfns'
      if (this.canViewProductAdmin) return 'admin-products'
      if (this.canAccessQaTools) return 'admin-qa'
      if (this.canAccessMarketingAdmin) return 'admin-source-codes'
      return null
    },
    getPin: ({pinNav}) => pinNav,
    loggedIn: ({token}) => token ? true : false,
    getTeams: ({teams}) => teams,
    getTeamById: ({ teams }) => (teamId) => teams.find(team => team.team_id === teamId),
    getUserRoles: ({ roles }) => roles
  }
})
